import { Paper, Tooltip } from '@mui/material';
import { GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import {
  DataTable,
  IColDef,
} from 'src/app/modules/Manage/pages/queues-table/DataTable';
import { endingDate } from '../../recoil/atoms/endingDate';
import { startingDate } from '../../recoil/atoms/startingDate';
import { Role, user } from '../../recoil/atoms/user';
import {
  makePHPRequest,
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import { permissionsSelector } from '../../recoil/selectors/permissionsSelector';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../services/rest/makeAPIRequest';
import { QueueEditDialog } from '../modules/Manage/pages/queue-edit-dialog/QueueEditDialog';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import { EnhancedTableToolbar } from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';
import { DashboardTourExtraSpotlights } from '../pages/dashboardTour/DashboardTourExtraSpotlights';

const pad = (num) => {
  return ('0' + num).slice(-2);
};
const hhmmss = (secs) => {
  let minutes = Math.floor(secs / 60);
  secs = secs % 60;
  const hours = Math.floor(minutes / 60);
  minutes = minutes % 60;
  return `${hours}:${pad(minutes)}:${pad(secs)}`;
};
const sortModel: GridSortModel = [
  {
    field: 'queuename',
    sort: 'asc',
  },
];

const getPercent = (num) => num.split(/[(]+/).pop().split(/[)]+/)[0];

const sortPercent = (a, b) => {
  a = getPercent(a);
  b = getPercent(b);
  return Number(a.replaceAll('%', '')) - Number(b.replaceAll('%', ''));
};

const padDuration = (d) => (Number(d.split(':')[0]) < 10 ? '0' + d : d);

const sortDuration = (a, b) =>
  String(padDuration(a)).localeCompare(String(padDuration(b)));

export function QueuesReport(props) {
  const startDate = useRecoilValueLoadable(startingDate);
  const endDate = useRecoilValueLoadable(endingDate);
  const userAtom = useRecoilValueLoadable(user);
  const bData = {
    call: methodNameEnum.GET_BUSINESS_DATA,
  };
  const businessData = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify(bData),
    })
  );
  const [rowForEdit, handleRowForEdit] = useState<{ queueid?: string }>({});
  const [queueEditModal, handleQueueEditModal] = useState(false);
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { role } = permissionsLoadable.contents;
  const isUser = role === Role.USER;
  const handleEdit = (row) => {
    handleQueueEditModal(true);
    handleRowForEdit(row);
  };
  function nameFormatter(params: GridRenderCellParams) {
    return isUser ? (
      params.value
    ) : (
      <Tooltip title="Edit">
        <Link
          onClick={() => {
            handleEdit(params.row);
          }}
          to="#"
        >
          {params.value}
        </Link>
      </Tooltip>
    );
  }
  const columns = [
    {
      field: 'queueid',
      headerName: 'Queue Id',
      headerClassName: dataTableScss.fullWidth + ' superStyle',
      cellClassName: dataTableScss.fullWidth,
      // make queue id column hidden from users that are not a superadmin
      hide: userAtom?.contents?.user?.role !== 'superadmin',
    },
    {
      field: 'queuename',
      headerName: 'Name',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: nameFormatter,
    },
    {
      field: 'abandonedOut',
      headerName: 'Abandoned',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      sortComparator: sortPercent,
    },
    {
      field: 'abandontime',
      headerName: 'Avg. Time to Abandon',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      sortComparator: sortDuration,
    },
    {
      field: 'holdtime',
      headerName: 'Avg. Hold Time',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      sortComparator: sortDuration,
    },

    {
      field: 'ringtime',
      headerName: 'Avg. Ring Time',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      sortComparator: sortDuration,
    },
    {
      field: 'missedOut',
      headerName: 'Missed',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      sortComparator: sortPercent,
    },
    {
      field: 'answeredOut',
      headerName: 'Answered',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      sortComparator: sortPercent,
    },
  ] /* satisfies */ as IColDef[];
  const refreshToggleForQueuesReporting = useSetRecoilState(
    refetchToggle(methodNameEnum.GET_QUEUES_REPORTING)
  );
  const data = useRecoilValueLoadable(
    makePHPRequest({
      methodName: methodNameEnum.GET_QUEUES_REPORTING,
      payloadJsonString: JSON.stringify({
        startdate: startDate.contents,
        enddate: endDate.contents,
        timezone:
          businessData?.contents?.response?.data?.result?.[0]?.timezone_php,
      }),
    })
  );
  const rows = useMemo(
    () =>
      data.contents?.data?.result
        ?.map((o) => ({
          ...o,
          id: o.queueid,
          queueid: Number(o.queueid),
          queuename: o?.queuename || 'no name',
          holdtime: hhmmss(Math.ceil(o['avg total hold'])),
          abandontime: hhmmss(Math.ceil(o['avg time to abandon'])),
          ringtime: hhmmss(Math.ceil(o['avg total ring'])),
          holdtimesort: Math.ceil(o['avg total hold']),
          abandontimesort: Math.ceil(o['avg time to abandon']),
          ringtimesort: Math.ceil(o['avg total ring']),
          abandoned: o.abandoned || 0,
          missed: o.missed || 0,
          answered: o.answered || 0,
          total: o.answered + o.missed + o.abandoned,
        }))
        .map((obj) => ({
          ...obj,
          answeredPercent: Math.ceil((obj.answered / obj.total) * 100),
          missedPercent: Math.ceil((obj.missed / obj.total) * 100),
          abandonedPercent: Math.ceil((obj.abandoned / obj.total) * 100),
        }))
        .map((obj) => ({
          ...obj,
          abandonedOut: `${obj.abandoned} (${obj.abandonedPercent}%)`,
          answeredOut: `${obj.answered} (${obj.answeredPercent}%)`,
          missedOut: `${obj.missed} (${obj.missedPercent}%)`,
        }))
        .filter((q) => q.queueid !== 0 && q.queuename) || [],
    [data]
  );
  const ToolbarComponent = useCallback(
    (props) => <EnhancedTableToolbar dataType="QueuesReport" {...props} />,
    []
  );
  return (
    <>
      <DashboardTourExtraSpotlights />
      <Paper square={true} elevation={0}>
        <DataTable
          columns={columns}
          rows={rows}
          loading={data.state === 'loading'}
          sortModel={sortModel}
          ToolbarComponent={ToolbarComponent}
        />
        <QueueEditDialog
          show={!!queueEditModal}
          id={rowForEdit.queueid}
          onHide={() => {
            handleQueueEditModal(false);
            refreshToggleForQueuesReporting({});
          }}
        />
      </Paper>
    </>
  );
}
